import React, { useState } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Chip,
  Tooltip,
  Typography,
  Button,
  CircularProgress,
  Popover,
  TextField,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import UndoIcon from '@mui/icons-material/Undo';
import EditIcon from '@mui/icons-material/Edit';
import { useModelRegistry, ModelCatalogEntry, ModelSlot } from './useModelRegistry';

const SOURCE_COLOR: Record<string, 'success' | 'info' | 'warning' | 'default'> = {
  db: 'success',
  env: 'info',
  hardcoded: 'warning',
  none: 'default',
};

interface ModelDefaultsBarProps {
  /**
   * If provided, only show these slots. Default: text, image.
   * AI Styles screen passes ['text', 'image']; Grok Imagine screen passes ['video', 'scene_analysis', 'scene_analysis_fallback'].
   */
  slots?: ModelSlot[];
}

const SLOT_LABELS: Record<ModelSlot, string> = {
  text: 'Writing model',
  text_fast: 'Writing model (fast)',
  image: 'Illustration model',
  video: 'Video model',
  scene_analysis: 'Scene analysis model',
  scene_analysis_fallback: 'Scene analysis fallback',
};

const SLOT_CAPABILITY: Record<ModelSlot, 'text' | 'image' | 'video'> = {
  text: 'text',
  text_fast: 'text',
  image: 'image',
  video: 'video',
  scene_analysis: 'text',
  scene_analysis_fallback: 'text',
};

function formatSyncAge(hours: number | null): string {
  if (hours === null || !Number.isFinite(hours)) return 'never';
  if (hours < 1) return `${Math.round(hours * 60)} min ago`;
  if (hours < 48) return `${hours.toFixed(1)} h ago`;
  return `${Math.floor(hours / 24)} d ago`;
}

const PRICING_UNITS = ['per 1M input tokens', 'per image', 'per second'] as const;

const ModelDefaultsBar: React.FC<ModelDefaultsBarProps> = ({ slots = ['text', 'image'] }) => {
  const {
    loading,
    updating,
    lastSyncedAt,
    stale,
    syncAgeHours,
    refreshModels,
    updateDefault,
    rollback,
    modelsByCapability,
    formatCost,
    effectiveModel,
    updatePricing,
  } = useModelRegistry();

  const [priceAnchor, setPriceAnchor] = useState<{ el: HTMLElement; modelId: string; unit: string; amount: string } | null>(null);

  return (
    <Box
      sx={{
        bgcolor: '#fff',
        borderBottom: '1px solid #e2e8f0',
        px: 3,
        py: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        flexWrap: 'wrap',
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{ fontFamily: '"Montserrat", sans-serif', fontWeight: 700, color: '#1e293b', mr: 1 }}
      >
        xAI model defaults
      </Typography>

      {loading && <CircularProgress size={18} />}

      {!loading && slots.map((slot) => {
        const capable = modelsByCapability(SLOT_CAPABILITY[slot]);
        const { value, source } = effectiveModel(slot);
        const selectedModel = capable.find((m: ModelCatalogEntry) => m.id === value);
        return (
          <Box key={slot} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <FormControl size="small" sx={{ minWidth: 260 }} disabled={updating}>
              <InputLabel id={`slot-${slot}-label`}>{SLOT_LABELS[slot]}</InputLabel>
              <Select
                labelId={`slot-${slot}-label`}
                label={SLOT_LABELS[slot]}
                value={value || ''}
                onChange={(e) => {
                  const next = String(e.target.value || '');
                  if (next && next !== value) updateDefault(slot, next);
                }}
              >
                {capable.map((m: ModelCatalogEntry) => (
                  <MenuItem key={m.id} value={m.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <Typography sx={{ fontSize: '0.85rem', flex: 1 }}>{m.id}</Typography>
                      <Chip
                        size="small"
                        label={m.tier}
                        sx={{ height: 18, fontSize: '0.6rem', textTransform: 'uppercase' }}
                      />
                      <Typography sx={{ fontSize: '0.7rem', color: '#64748b', minWidth: 70, textAlign: 'right' }}>
                        {formatCost(m)}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Tooltip title={`Source: ${source === 'db' ? 'Admin selection' : source === 'env' ? 'Environment variable' : source === 'hardcoded' ? 'Hardcoded fallback' : 'Unknown'}`}>
              <Chip
                size="small"
                color={SOURCE_COLOR[source] || 'default'}
                label={source}
                sx={{ height: 20, fontSize: '0.6rem', textTransform: 'uppercase' }}
              />
            </Tooltip>
            <Tooltip title="Revert last change">
              <span>
                <IconButton size="small" onClick={() => rollback(slot)} disabled={updating || source !== 'db'}>
                  <UndoIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
            {selectedModel && (
              <Tooltip title={`Override pricing for ${selectedModel.id}`}>
                <IconButton
                  size="small"
                  onClick={(e) => setPriceAnchor({
                    el: e.currentTarget,
                    modelId: selectedModel.id,
                    unit: selectedModel.pricing?.unit ?? PRICING_UNITS[SLOT_CAPABILITY[slot] === 'video' ? 2 : SLOT_CAPABILITY[slot] === 'image' ? 1 : 0],
                    amount: selectedModel.pricing ? String(selectedModel.pricing.amount) : '',
                  })}
                  disabled={updating}
                >
                  <EditIcon sx={{ fontSize: 14, color: selectedModel.pricing ? '#94a3b8' : '#f59e0b' }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      })}

      <Box sx={{ flex: 1 }} />

      <Tooltip
        title={
          lastSyncedAt
            ? `${stale ? 'Last xAI sync > 96h ago' : 'Synced from xAI'} — ${formatSyncAge(syncAgeHours)} (at ${new Date(lastSyncedAt).toLocaleString()})`
            : 'No sync recorded yet — click Refresh'
        }
      >
        <Box
          aria-label={stale ? 'catalog-stale' : 'catalog-fresh'}
          sx={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            bgcolor: stale ? '#f59e0b' : lastSyncedAt ? '#10b981' : '#94a3b8',
            flexShrink: 0,
          }}
        />
      </Tooltip>

      <Tooltip title="Sync xAI model catalog (rate-limited: 1 per 30s)">
        <span>
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
            onClick={refreshModels}
            disabled={updating}
          >
            Refresh
          </Button>
        </span>
      </Tooltip>

      {/* Pricing override popover */}
      <Popover
        open={Boolean(priceAnchor)}
        anchorEl={priceAnchor?.el}
        onClose={() => setPriceAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        {priceAnchor && (
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 260 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#1e293b' }}>
              Override pricing — {priceAnchor.modelId}
            </Typography>
            <FormControl size="small" fullWidth>
              <InputLabel>Unit</InputLabel>
              <Select
                label="Unit"
                value={priceAnchor.unit}
                onChange={(e) => setPriceAnchor(p => p ? { ...p, unit: String(e.target.value) } : null)}
              >
                {PRICING_UNITS.map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Amount (USD)"
              type="number"
              inputProps={{ min: 0, step: 0.001 }}
              value={priceAnchor.amount}
              onChange={(e) => setPriceAnchor(p => p ? { ...p, amount: e.target.value } : null)}
            />
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button size="small" onClick={() => setPriceAnchor(null)}>Cancel</Button>
              <Button
                size="small"
                variant="contained"
                disabled={updating || !priceAnchor.amount || isNaN(Number(priceAnchor.amount))}
                onClick={async () => {
                  await updatePricing(priceAnchor.modelId, priceAnchor.unit, Number(priceAnchor.amount));
                  setPriceAnchor(null);
                }}
              >
                Save
              </Button>
            </Box>
          </Box>
        )}
      </Popover>
    </Box>
  );
};

export default ModelDefaultsBar;
